import React, { useRef, useState } from "react";
import Button from "@amzn/meridian/esm/components/button";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Popover from "@amzn/meridian/esm/components/popover";
import RadioButton from "@amzn/meridian/esm/components/radio-button";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Textarea from "@amzn/meridian/esm/components/textarea";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import FeedbackButtons from "./FeedbackButtons";

import LogManager from "../../utils/logManager";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import FeedbackPopoverTop from "../../images/feedback_popover_top.svg";
import feedbackNotSubmitted from "../../images/feedback_not_submitted.svg";
import feedbackSubmitted from "../../images/feedback_submitted.svg";
import { FEEDBACK } from '../../constants';
import { FEEDBACK_MESSAGE, FEEDBACK_REASON, FEEDBACK_TYPE } from './feedback.constants';

/**
 * Feedback component for meeting summaries with thumbs up/down options.
 * Handles user feedback submission and API interaction.
 *
 * @param {string} aimsUuid - UUID for the meeting summary
 * @returns {JSX.Element} Summary feedback UI including ButtonGroup and Popover
 */

const SummaryFeedback = ({ aimsUuid }) => {
    const popoverRef = useRef()
    const [feedback, setFeedback] = useState(undefined);
    const [isFeedbackDisabled, setIsFeedbackDisabled] = useState(false);
    const [feedbackReason, setFeedbackReason] = useState(undefined);
    const [feedbackComment, setFeedbackComment] = useState(undefined);
    const [thumbsDownPopoverOpen, setThumbsDownPopoverOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const onCloseSubmittedPopover = () => {
        setThumbsDownPopoverOpen(false);
        setFeedbackReason(undefined);
        setFeedbackComment(undefined);
        setIsSubmitted(false); // Reset the submitted state when pop over is closed
    };

    const onClosePopover = () => {
        onCloseSubmittedPopover();
        setFeedback(undefined);
        setIsFeedbackDisabled(false);
    }

    const handleThumbsClick = async (feedbackValue) => {
        if (!isFeedbackDisabled) {
            if (feedbackValue === FEEDBACK.THUMBS_DOWN) {
                setFeedbackReason(undefined);
                setFeedbackComment(undefined);
                setThumbsDownPopoverOpen(true);
            } else {
                setThumbsDownPopoverOpen(false);
                setIsFeedbackDisabled(true);
                setFeedback(feedbackValue);
                const success = await submitFeedback(feedbackValue, null, null);
                if (!success) {
                    setIsFeedbackDisabled(false);
                    setFeedback(undefined);
                }
            }
        }
    };

    const submitFeedback = async (feedback, reason = feedbackReason, comment = feedbackComment) => {
        LogManager.info("Calling /feedback API with aimsUuid: " + aimsUuid);
        try {
            const body = {
                type: FEEDBACK_TYPE.SUMMARY,
                aimsUuid: aimsUuid,
                feedback: feedback,
                feedbackTimestamp: Math.floor(Date.now() / 1000),
            };
            if (reason) body.feedbackReason = reason;
            if (comment) body.feedbackComment = comment;
            const response = await fetch('/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                LogManager.error('Feedback API returned error status:', response.status);
                return false;
            }
            const responseJson = await response.json();
            LogManager.info("The response of /feedback is:", responseJson);
            return true;
        } catch (error) {
            LogManager.error('Error submitting feedback:', error);
            return false;
        }
    };

    const handlePopoverSubmit = async () => {
        setIsFeedbackDisabled(true);
        setFeedback(FEEDBACK.THUMBS_DOWN);
        setIsSubmitted(true);
        const success = await submitFeedback(FEEDBACK.THUMBS_DOWN);
        if (!success) {
            setIsFeedbackDisabled(false);
            setFeedback(undefined);
            setIsSubmitted(false);
        }
    };

    return (
        <>
            {/* Thumbs up/down button */}
            <FeedbackButtons
                feedback={feedback}
                disabled={isFeedbackDisabled}
                onThumbsUp={() => handleThumbsClick(FEEDBACK.THUMBS_UP)}
                onThumbsDown={() => handleThumbsClick(FEEDBACK.THUMBS_DOWN)}
            />
            {/* Thumbs down popover */}
            <Column alignmentHorizontal="center">
                <Row ref={popoverRef}/>
                {thumbsDownPopoverOpen &&
                    <Theme palette={aimsTheme}>
                        <Popover
                            open={true}
                            minWidth="292px"
                            anchorNode={popoverRef.current}
                            onClose={()=>{}}
                            position="top"
                            indicatorArrow={false}
                            spacingInset="none"
                        >
                            <Column heights={["20%","65%","15%"]} height="370px" spacing="none">
                                {isSubmitted && feedback === FEEDBACK.THUMBS_DOWN ? 
                                    <>
                                        <div                     
                                            style={{
                                                background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                                width: "100%",
                                            }}
                                        >
                                            <Row widths={["80%","20%"]} alignmentVertical="bottom" height="50px">
                                                <div/>
                                                <Button type="icon" onClick={onCloseSubmittedPopover}>
                                                    <Icon tokens={closeSmallTokens}/>
                                                </Button>
                                            </Row>
                                        </div>
                                        <Column alignmentHorizontal="center" alignmentVertical="center">
                                            {feedbackComment || feedbackReason ? 
                                            <>
                                                <img
                                                    src={feedbackSubmitted}
                                                    style={{width: '100px', height: 'auto', maxWidth: '100%'}}
                                                />
                                                <Text type="h50">
                                                    {FEEDBACK_MESSAGE.SUBMITTED}
                                                </Text>
                                            </>
                                            :
                                            <>
                                                <img
                                                    src={feedbackNotSubmitted}
                                                    style={{width: '60px', height: 'auto', maxWidth: '100%'}}
                                                />
                                                <Text type="h50">
                                                    {FEEDBACK_MESSAGE.NOT_SUBMITTED}
                                                </Text>
                                            </>
                                            }
                                        </Column>
                                        <Row alignmentHorizontal="end" spacingInset="none 500 none none">
                                            <Button type="primary" size="small" minWidth="90px" onClick={onCloseSubmittedPopover}>Close</Button>
                                        </Row>
                                    </>
                                :
                                    <>
                                        <div                     
                                            style={{
                                                background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                                width: "100%",
                                            }}
                                        >
                                            <Row widths={["10%", "70%","20%"]} alignmentVertical="bottom" height="50px">
                                                <div/>
                                                <Text type="h100">
                                                    Share Feedback
                                                </Text>
                                                <Button type="icon" onClick={onClosePopover}>
                                                    <Icon tokens={closeSmallTokens}/>
                                                </Button>
                                            </Row>
                                        </div>
                                        <Column spacingInset="500 400 400 400" spacing="500">
                                            <Row alignmentHorizontal="center">
                                                <Column spacing="300">
                                                    <RadioButton
                                                        checked={feedbackReason === FEEDBACK_REASON.NOT_ACCURATE}
                                                        onChange={setFeedbackReason}
                                                        name="feedbackReason"
                                                        value={FEEDBACK_REASON.NOT_ACCURATE}
                                                        size="small"
                                                    >
                                                        {FEEDBACK_REASON.NOT_ACCURATE}
                                                    </RadioButton>
                                                    <RadioButton
                                                        checked={feedbackReason === FEEDBACK_REASON.NOT_COMPLETE}
                                                        onChange={setFeedbackReason}
                                                        name="feedbackReason"
                                                        value={FEEDBACK_REASON.NOT_COMPLETE}
                                                        size="small"
                                                    >
                                                        {FEEDBACK_REASON.NOT_COMPLETE}
                                                    </RadioButton>
                                                    <RadioButton
                                                        checked={feedbackReason === FEEDBACK_REASON.BLOCKED}
                                                        onChange={setFeedbackReason}
                                                        name="feedbackReason"
                                                        value={FEEDBACK_REASON.BLOCKED}
                                                        size="small"
                                                    >
                                                        {FEEDBACK_REASON.BLOCKED}
                                                    </RadioButton>
                                                </Column>
                                            </Row>
                                            <Row alignmentHorizontal="center">
                                                <Textarea
                                                    value={feedbackComment}
                                                    onChange={setFeedbackComment}
                                                    placeholder="Tell us more"
                                                    error={false}
                                                    width="230px"
                                                    rows={3}
                                                    size="small"
                                                />
                                            </Row>
                                        </Column>
                                        <Row alignmentHorizontal="end" spacingInset="none 500 none none">
                                            <Button type="primary" size="small" minWidth="90px" onClick={handlePopoverSubmit}>
                                                Submit
                                            </Button>
                                        </Row>
                                    </>
                                }
                            </Column>
                        </Popover>
                    </Theme>
                }
            </Column>
        </>
    );
};

export default SummaryFeedback;